import { useEffect, useState, type KeyboardEvent } from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';

import { Field, Modal, Notice } from '@/components/Modal';
import { apiFetch } from '@/lib/api/client';
import type { PasswordResetChallenge } from '@/lib/auth/session';
import { queryKeys } from '@/lib/queryKeys';

function useSecondsLeft(deadline: number | null): number {
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (deadline === null) return;
    const timer = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(timer);
  }, [deadline]);
  return deadline === null ? 0 : Math.max(0, Math.ceil((deadline - now) / 1000));
}

function formatSeconds(seconds: number): string {
  return `${Math.floor(seconds / 60)}:${String(seconds % 60).padStart(2, '0')}`;
}

/**
 * 내 휴대폰 번호 등록·변경 (사용자 지시 2026-10-07).
 *
 * 비밀번호 초기화의 인증번호가 이 번호로 간다 — 그래서 본인이 넣고, 그 번호로 온 인증번호를 맞혀야
 * 인사자료 연락처에 저장된다(서버 `MyPhoneService`).
 */
export function MyPhoneModal({ current, onClose }: { current: string; onClose: () => void }) {
  const queryClient = useQueryClient();
  const [phone, setPhone] = useState(current);
  const [challenge, setChallenge] = useState<PasswordResetChallenge | null>(null);
  const [deadline, setDeadline] = useState<number | null>(null);
  const [code, setCode] = useState('');
  const secondsLeft = useSecondsLeft(deadline);

  const start = useMutation({
    mutationFn: () =>
      apiFetch<PasswordResetChallenge>('/v1/home/profile/phone/verification', { method: 'POST', body: { phone } }),
    onSuccess: (sent) => {
      setChallenge(sent);
      setDeadline(Date.now() + sent.expiresInSeconds * 1000);
      setCode('');
    },
  });

  const confirm = useMutation({
    mutationFn: () =>
      apiFetch<string>('/v1/home/profile/phone', {
        method: 'PUT',
        body: { verificationId: challenge?.verificationId, code },
      }),
    onSuccess: async () => {
      await queryClient.invalidateQueries({ queryKey: queryKeys.homeProfile });
      onClose();
    },
  });

  const pending = start.isPending || confirm.isPending;
  const canSend = phone.trim() !== '' && !pending;
  const canConfirm = challenge !== null && code !== '' && secondsLeft > 0 && !pending;

  const send = () => {
    if (!canSend) return;
    confirm.reset();
    start.mutate();
  };
  const verify = () => {
    if (canConfirm) confirm.mutate();
  };

  const onEnter = (action: () => void) => (event: KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter' && !event.nativeEvent.isComposing) action();
  };

  const error = confirm.error ?? start.error;

  return (
    <Modal
      title="연락처 변경"
      maxWidth="420px"
      onClose={() => !pending && onClose()}
      closeDisabled={pending}
      footer={
        <>
          <button onClick={onClose} className="btn-secondary" disabled={pending}>
            취소
          </button>
          <button onClick={send} className={challenge ? 'btn-secondary' : 'btn-primary'} disabled={!canSend}>
            {start.isPending ? '보내는 중...' : challenge ? '다시 받기' : '인증번호 받기'}
          </button>
          {challenge && (
            <button onClick={verify} className="btn-primary" disabled={!canConfirm}>
              {confirm.isPending ? '저장 중...' : '확인'}
            </button>
          )}
        </>
      }
    >
      <Field label="휴대폰 번호">
        <input
          className="input-field"
          inputMode="tel"
          autoComplete="tel"
          value={phone}
          onChange={(event) => {
            setPhone(event.target.value);
            setChallenge(null);
            setDeadline(null);
          }}
          onKeyDown={onEnter(send)}
          disabled={pending}
        />
      </Field>
      {challenge && (
        <Field label={`인증번호 · ${challenge.maskedPhone} · ${formatSeconds(secondsLeft)}`}>
          <input
            className="input-field"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))}
            onKeyDown={onEnter(verify)}
            disabled={pending}
            autoFocus
          />
        </Field>
      )}
      {error && <Notice tone="error">{error.message}</Notice>}
    </Modal>
  );
}
